import React, { useState, useCallback, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { parseMLSPhotoFolder } from './photoUrls';
import { usePhotoActions } from './usePhotoActions';
import { usePhotoCarousel } from './usePhotoCarousel';
import { usePhotoImage } from './usePhotoImage';
import { useDragAndDrop } from './useDragAndDrop';
import { PhotoOverlayArrows } from './PhotoNavArrows';
import PhotoUploadDialog from './PhotoUploadDialog';
import PhotoDeleteConfirmDialog from './PhotoDeleteConfirmDialog';
import PhotoDetailDialog from './PhotoDetailDialog';

// Main PhotoCell component
const PhotoCell = ({
  parcelId,
  MLSPhotoFolder,
  onPhotoUpdate,
  style = {},
  cellMode = 'display', // 'display' or 'edit' - controls if editing features are available
  showUploadPrompt = true, // whether to show "Upload Photo" text for empty cells
  arrowSize = 'normal' // 'compact' shrinks the grid nav arrows for especially small cells
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [enlargedPhotoOpen, setEnlargedPhotoOpen] = useState(false);
  const [enlargedPhotoLoading, setEnlargedPhotoLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [uploadPromptHovered, setUploadPromptHovered] = useState(false);

  const mlsPhotos = useMemo(() => parseMLSPhotoFolder(MLSPhotoFolder), [MLSPhotoFolder]);
  const hasMlsPhotos = mlsPhotos.length > 0;

  const grid = usePhotoCarousel(mlsPhotos);
  const enlarged = usePhotoCarousel(mlsPhotos);
  const { uploading: isUploading, uploadNewPhoto, setPrimaryPhoto, removePhoto } = usePhotoActions(parcelId, onPhotoUpdate);
  // Live/default photo is always the canonical S3 primary slot — not row PhotoURL
  // (row PhotoURL is often a stale MLS listing URL from the data feed).
  const { actualUrl: actualPhotoUrl, hasPhoto, imageLoading } = usePhotoImage(parcelId);

  const gridDisplayUrl = grid.index === -1 ? actualPhotoUrl : mlsPhotos[grid.index];
  const enlargedDisplayUrl = enlarged.index === -1 ? actualPhotoUrl : mlsPhotos[enlarged.index];

  useEffect(() => {
    grid.reset();
    enlarged.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId, MLSPhotoFolder]);

  const handlePhotoUpload = useCallback(async (file) => {
    setUploadDialogOpen(false);
    const newUrl = await uploadNewPhoto(file);
    if (newUrl) {
      setEnlargedPhotoOpen(false);
    }
  }, [uploadNewPhoto]);

  const handleSetPrimaryPhoto = useCallback(async () => {
    const sourceUrl = mlsPhotos[enlarged.index];
    if (!sourceUrl) return;

    const newUrl = await setPrimaryPhoto(sourceUrl);
    if (newUrl) {
      grid.reset();
      enlarged.reset();
      setEnlargedPhotoOpen(false);
    }
  }, [mlsPhotos, enlarged, grid, setPrimaryPhoto]);

  const handlePrimaryPhotoAction = useCallback(() => {
    if (enlarged.index === -1) {
      setEnlargedPhotoOpen(false);
      setTimeout(() => {
        setUploadDialogOpen(true);
      }, 100);
      return;
    }
    handleSetPrimaryPhoto();
  }, [enlarged.index, handleSetPrimaryPhoto]);

  const handlePhotoDelete = useCallback(async () => {
    await removePhoto();
    setDeleteConfirmOpen(false);
  }, [removePhoto]);

  const { dragActive, dragHandlers } = useDragAndDrop(handlePhotoUpload);
  const cellDragHandlers = cellMode === 'edit' ? dragHandlers : {};

  // Handle upload dialog opening
  const handleOpenUploadDialog = useCallback(() => {
    if (cellMode !== 'edit') return;
    setUploadDialogOpen(true);
  }, [cellMode]);

  const gridPhotoNav = hasMlsPhotos ? (
    <PhotoOverlayArrows onPrev={grid.goPrev} onNext={grid.goNext} size={arrowSize} />
  ) : null;

  // Handle photo enlargement
  const handleEnlargePhoto = useCallback((e) => {
    if (!hasPhoto && !(hasMlsPhotos && grid.index >= 0)) return;
    e?.stopPropagation();
    enlarged.setIndex(grid.index);
    setEnlargedPhotoLoading(true);
    setEnlargedPhotoOpen(true);
  }, [hasPhoto, hasMlsPhotos, grid.index, enlarged]);

  // Handle delete confirmation
  const handleOpenDeleteConfirm = useCallback((e) => {
    if (cellMode !== 'edit') return;
    e?.stopPropagation();
    setDeleteConfirmOpen(true);
  }, [cellMode]);

  // Always render dialogs outside conditional logic
  return (
    <>
      {/* Main content based on photo state */}
      {imageLoading ? (
        <div
          className="flex items-center justify-center w-full h-full gap-2"
          style={{ ...style }}
        >
          <CircularProgress
            size={16}
            sx={{
              color: '#666',
              '& .MuiCircularProgress-circle': {
                strokeWidth: 3
              }
            }}
          />
        </div>
      ) : !hasPhoto && !(hasMlsPhotos && grid.index >= 0) ? (
        isUploading ? (
          <div
            className="flex items-center justify-center w-full h-full gap-2"
            style={{ ...style }}
          >
            <CircularProgress
              size={16}
              sx={{
                color: '#000000',
                '& .MuiCircularProgress-circle': {
                  strokeWidth: 3
                }
              }}
            />
            <span style={{ fontSize: '11px', color: '#000000' }}>Uploading</span>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center w-full h-full"
            style={{
              cursor: cellMode === 'edit' ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              outline: dragActive ? '1px solid #1976d2' : 'none',
              ...style
            }}
            {...cellDragHandlers}
          >
            {cellMode === 'edit' && (
              <div
                onClick={handleOpenUploadDialog}
                onMouseEnter={() => setUploadPromptHovered(true)}
                onMouseLeave={() => setUploadPromptHovered(false)}
                style={{
                  cursor: 'pointer',
                  padding: '0px 4px',
                  color: '#d32f2f',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '20px',
                  transition: 'all 0.2s ease',
                  backgroundColor: (dragActive || uploadPromptHovered) ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                  margin: '2px 0',
                  border: `1px dashed ${(dragActive || uploadPromptHovered) ? '#1976d2' : '#ccc'}`,
                  borderRadius: '4px'
                }}
              >
                {showUploadPrompt ? 'Upload Photo' : '+'}
              </div>
            )}
            {cellMode === 'display' && (
              <div
                style={{
                  color: '#999',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '20px'
                }}
              >
                No Photo
              </div>
            )}
          </div>
        )
      ) : (
        // Render photo cell with image
        <div style={{ ...style, position: 'relative', width: '100%', height: '100%' }}>
          <div
            className="relative w-full h-full group flex items-center justify-center"
            style={{ cursor: 'pointer' }}
            onClick={handleEnlargePhoto}
            {...cellDragHandlers}
          >
            <img
              key={gridDisplayUrl}
              src={gridDisplayUrl}
              alt="Property"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 2
              }}
            />

            {gridPhotoNav}

            {/* Delete used to sit on the thumbnail. It now lives in PhotoDetailDialog. */}
            {/* {cellMode === 'edit' && grid.index === -1 && (
              <button
                onClick={handleOpenDeleteConfirm}
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  background: 'rgba(211, 47, 47, 0.9)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  cursor: 'pointer',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  padding: 0
                }}
                className="group-hover:opacity-100"
              >
                ×
              </button>
            )} */}
          </div>
        </div>
      )}

      {/* Upload Dialog - Always rendered */}
      <PhotoUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handlePhotoUpload}
        uploading={isUploading}
      />

      {/* Delete Confirmation Dialog - Always rendered */}
      <PhotoDeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handlePhotoDelete}
      />

      {/* Enlarged Photo Dialog - Always rendered */}
      <PhotoDetailDialog
        open={enlargedPhotoOpen}
        onClose={() => setEnlargedPhotoOpen(false)}
        onExited={() => {
          enlarged.reset();
          setEnlargedPhotoLoading(false);
        }}
        photoUrl={enlargedDisplayUrl}
        imgLoading={enlargedPhotoLoading}
        onImgLoad={() => setEnlargedPhotoLoading(false)}
        onImgError={() => setEnlargedPhotoLoading(false)}
        nav={hasMlsPhotos ? { onPrev: enlarged.goPrev, onNext: enlarged.goNext, current: enlarged.displayNumber, total: enlarged.total } : null}
        primaryAction={cellMode === 'edit' ? {
          label: isUploading ? 'Saving...' : (enlarged.index === -1 ? 'Upload New Photo' : 'Set as Primary Photo'),
          onClick: handlePrimaryPhotoAction,
          disabled: isUploading,
        } : null}
        secondaryAction={cellMode === 'edit' && enlarged.index === -1 ? {
          label: 'Delete photo',
          onClick: handleOpenDeleteConfirm,
          disabled: isUploading,
        } : null}
      />
    </>
  );
};

export default PhotoCell;
